import React, { Fragment, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import axios from 'axios';

import { useAuth } from '../../../components/auth/AuthProvider';
import { useLanguage } from '../../../i18n/LanguageContext';
import apiUrl from '../../../config/api';

const getToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');

const AccountSetting = () => {
  const { userDetails, updateUserDetails, logoutMe } = useAuth();
  const { currentLanguage, t } = useLanguage();
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [deleteForm, setDeleteForm] = useState({
    password: '',
    confirmation: '',
  });
  const [feedback, setFeedback] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const deleteConfirmationText = currentLanguage.code === 'en' ? 'DELETE' : 'ELIMINA';

  useEffect(() => {
    setProfileForm({
      firstName: userDetails.firstName || '',
      lastName: userDetails.lastName || '',
      email: userDetails.email || '',
      phone: userDetails.phone || '',
    });
  }, [userDetails]);

  const authHeaders = () => ({
    headers: {
      Authorization: `Bearer ${getToken()}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  const showFeedback = (variant, message) => {
    setFeedback({ variant, message });
  };

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setProfileForm((previousForm) => ({ ...previousForm, [name]: value }));
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordForm((previousForm) => ({ ...previousForm, [name]: value }));
  };

  const handleDeleteChange = (event) => {
    const { name, value } = event.target;
    setDeleteForm((previousForm) => ({ ...previousForm, [name]: value }));
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();

    try {
      setIsSavingProfile(true);
      const response = await axios.put(`${apiUrl}/account`, profileForm, authHeaders());
      updateUserDetails(response.data.user);
      showFeedback('success', t.account.profileSaved);
    } catch (error) {
      showFeedback('danger', error.response?.data?.message || t.account.genericError);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    try {
      setIsSavingPassword(true);
      await axios.put(`${apiUrl}/account/password`, passwordForm, authHeaders());
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showFeedback('success', t.account.passwordSaved);
    } catch (error) {
      showFeedback('danger', error.response?.data?.message || t.account.genericError);
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleDeleteAccount = async (event) => {
    event.preventDefault();

    try {
      setIsDeletingAccount(true);
      await axios.delete(`${apiUrl}/account`, {
        ...authHeaders(),
        data: deleteForm,
      });
      showFeedback('success', t.account.accountDeleted);
      logoutMe();
    } catch (error) {
      showFeedback('danger', error.response?.data?.message || t.account.genericError);
      setIsDeletingAccount(false);
    }
  };

  const isDeleteDisabled = (
    isDeletingAccount ||
    deleteForm.confirmation.trim().toUpperCase() !== deleteConfirmationText ||
    !deleteForm.password
  );

  return (
    <Fragment>
      <Row>
        <Col lg="12">
          <div className="card">
            <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div>
                <h3 className="mb-1">{t.account.title}</h3>
                <p className="mb-0 text-muted">{t.account.subtitle}</p>
              </div>
              <Badge bg="primary">{userDetails.role}</Badge>
            </div>
          </div>
        </Col>
      </Row>

      {feedback && (
        <Alert variant={feedback.variant} onClose={() => setFeedback(null)} dismissible>
          {feedback.message}
        </Alert>
      )}

      <Row>
        <Col lg="7">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title mb-0">{t.account.personalInfo}</h4>
            </div>
            <div className="card-body">
              <Form onSubmit={handleProfileSubmit}>
                <Row>
                  <Col md="6">
                    <Form.Group className="form-group">
                      <Form.Label>{t.account.firstName}</Form.Label>
                      <Form.Control
                        name="firstName"
                        value={profileForm.firstName}
                        onChange={handleProfileChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md="6">
                    <Form.Group className="form-group">
                      <Form.Label>{t.account.lastName}</Form.Label>
                      <Form.Control
                        name="lastName"
                        value={profileForm.lastName}
                        onChange={handleProfileChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md="6">
                    <Form.Group className="form-group">
                      <Form.Label>{t.account.email}</Form.Label>
                      <Form.Control
                        name="email"
                        type="email"
                        value={profileForm.email}
                        onChange={handleProfileChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md="6">
                    <Form.Group className="form-group">
                      <Form.Label>{t.account.phone}</Form.Label>
                      <Form.Control
                        name="phone"
                        value={profileForm.phone}
                        onChange={handleProfileChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Button type="submit" variant="primary" disabled={isSavingProfile}>
                  {isSavingProfile ? '...' : t.account.saveInfo}
                </Button>
              </Form>
            </div>
          </div>
        </Col>

        <Col lg="5">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title mb-0">{t.account.passwordTitle}</h4>
            </div>
            <div className="card-body">
              <Form onSubmit={handlePasswordSubmit}>
                <Form.Group className="form-group">
                  <Form.Label>{t.account.currentPassword}</Form.Label>
                  <Form.Control
                    name="currentPassword"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </Form.Group>
                <Form.Group className="form-group">
                  <Form.Label>{t.account.newPassword}</Form.Label>
                  <Form.Control
                    name="newPassword"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    minLength={8}
                    required
                  />
                </Form.Group>
                <Form.Group className="form-group">
                  <Form.Label>{t.account.confirmPassword}</Form.Label>
                  <Form.Control
                    name="confirmPassword"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    minLength={8}
                    required
                  />
                </Form.Group>
                <Button type="submit" variant="primary" disabled={isSavingPassword}>
                  {isSavingPassword ? '...' : t.account.updatePassword}
                </Button>
              </Form>
            </div>
          </div>

          <div className="card border border-danger">
            <div className="card-header">
              <h4 className="card-title mb-0 text-danger">{t.account.dangerTitle}</h4>
            </div>
            <div className="card-body">
              <p className="text-muted">{t.account.dangerText}</p>
              <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
                {t.account.deleteAccount}
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Modal className="modal-theme-bg account-delete-modal" show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Form onSubmit={handleDeleteAccount}>
          <Modal.Header closeButton>
            <Modal.Title>{t.account.confirmDeleteTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="danger">{t.account.confirmDeleteText}</Alert>
            <Form.Group className="form-group">
              <Form.Label>{t.account.currentPassword}</Form.Label>
              <Form.Control
                name="password"
                type="password"
                value={deleteForm.password}
                onChange={handleDeleteChange}
                required
              />
            </Form.Group>
            <Form.Group className="form-group">
              <Form.Label>{t.account.confirmationLabel}</Form.Label>
              <Form.Control
                name="confirmation"
                value={deleteForm.confirmation}
                onChange={handleDeleteChange}
                placeholder={deleteConfirmationText}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="soft-secondary" onClick={() => setShowDeleteModal(false)}>
              {t.account.cancel}
            </Button>
            <Button type="submit" variant="danger" disabled={isDeleteDisabled}>
              {isDeletingAccount ? '...' : t.account.confirmDelete}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Fragment>
  );
};

export default AccountSetting;
